import { useState, useEffect, useRef } from "react";
import { useGlobalContext } from "../Context/GlobalContext";
import { useNavigationContext } from "../Context/NavigationContext";
import { Grid2 as Grid } from '@mui/material';
import Button from "../Components/button";
import { Device } from "../Types";
import { motion } from "motion/react"
import { keepAlive } from "../Lib/Devices";
import Loading from "../Components/loading";

// Dashboard views
import DeviceList from "./Dashboard/DeviceList";
import AddDevice from "./Dashboard/AddDevice";
import EditDevice from "./Dashboard/EditDevice";
import Account from "./Dashboard/Account";
import FAQ from "./Dashboard/FAQ";
// import Toggle from "../Components/toggle";
import Notify from "../Utils/Notify";

const DashboardView = () => {
    const menuItems: any = {
        'Internal': [
            { text: 'MY PRODUCTS', onClick: () => setView('deviceList') },
            { text: 'MY ACCOUNT', onClick: () => setView('account') },
            { text: 'FAQ', onClick: () => setView('FAQ') },
            { text: 'LOG OUT', onClick: () => handleSignOut() },
        ],
        'External': [
            { text: 'SUPPORT', onClick: () => window.open('https://knogsupport.zendesk.com/hc/en-us', '_blank') },
            { text: 'SHOP', onClick: () => window.open('https://www.knog.com', '_blank') },
            { text: 'PRIVACY', onClick: () => window.open('https://www.knog.com/pages/privacy-policy', '_blank') }
        ]
    }

    const viewMenuItems: any = {
        'deviceList': 'MY PRODUCTS',
        'addDevice': 'MY PRODUCTS',
        'editDevice': 'MY PRODUCTS',
        'FAQ': 'FAQ',
        'account': 'MY ACCOUNT'
    }

    const { 
        dbManager, 
        // toggleTheme, 
        // theme, 
        usbManager, 
        showPopup, 
        hidePopup, 
        deviceManager,
        setConnectedDevices,
        getThemeImage
    } = useGlobalContext();

    const { navigateTo } = useNavigationContext();
    const [selectedDeviceID, setSelectedDeviceID] = useState<number | null>(null);
    const [dashboardView, setDashboardView] = useState<string>('deviceList');
    const [opacity, setOpacity] = useState<number>(0);
    const [initialised, setInitialised] = useState<boolean>(false);
    const [showLoadingScreen, setShowLoadingScreen] = useState(false);

    const dashboardViewRef = useRef(dashboardView);
    const selectedDeviceIDRef = useRef<number | null>(null);
    const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const getConnectedDevices = async () => {
        setConnectedDevices(await usbManager.updateDeviceList());
    }

    useEffect(() => {
        const flicker = () => {
            const pattern = [0.1, 0.2, 0.3, 0.4, 0.3, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
            pattern.forEach((value, index) => {
                setTimeout(() => {
                    setOpacity(value);
                }, index * 50);
            });
        }

        const startKeepAlive = () => {
            if (keepAliveIntervalRef.current) {
                clearInterval(keepAliveIntervalRef.current);
            }

            keepAliveIntervalRef.current = setInterval(() => {
                let hasCobber = false;
                deviceManager.devices.forEach((device: Device) => {
                    if (keepAlive(device.type.id)) hasCobber = true;
                });

                if (hasCobber) usbManager.updateDeviceList();
            }, 60000);
        };

        startKeepAlive()
        dashboardViewRef.current = dashboardView;
        if(!initialised) {
            getConnectedDevices();
            flicker();
            usbManager.registerEvent('disconnect', removeDevice);
            setInitialised(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardView]);

    useEffect(() => {
        selectedDeviceIDRef.current = selectedDeviceID;
    }, [selectedDeviceID]);

    const setView = (view: string) => {
        setShowLoadingScreen(false)
        setDashboardView(view);
    }

    const removeDevice = async (e: any) => {
        const prevDevices = usbManager.connectedDevices;
        await getConnectedDevices();
        // Check which device was disconnected
        const disconnectedDevice = prevDevices.find((device: any) => !usbManager.connectedDevices.includes(device));
        // If no device was disconnected, return
        if(!disconnectedDevice) return;
        handleDeviceDisconnect(disconnectedDevice);
    }

    const handleDeviceDisconnect = (hardwareId: string) => {
        usbManager.clear();
        const device = deviceManager.devices.find((device: Device) => device.hardwareId === hardwareId) ?? null;
        const currentSelectedID = selectedDeviceIDRef.current;

        if(dashboardViewRef.current === 'editDevice' && device?.id === currentSelectedID) {
            showPopup({
                title: "DEVICE DISCONNECTED",
                content: 
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        Please reconnect your device or return to the product list
                        <div className="reconnect-buttons">
                            <Button 
                                text="Return to List" 
                                onClick={handleReturn}
                                className={'reconnect-button'}
                            />
                            <Button 
                                text="Reconnect" 
                                onClick={
                                    () => handleEditDevice(device?.id ?? 0)}
                                className={'reconnect-button'}
                            />                   
                        </div>
                    </div>,
                noButtons: true
            });
        }
    }

    const handleReturn = () => {
        setView('deviceList');
        hidePopup();
    }

    const handleSignOut = async () => {
        await dbManager.logout();
        navigateTo('landing');
    }

    const handleEditDevice = async (deviceID: number) => {
        const device = deviceManager.devices.find((device: Device) => device.id === deviceID) ?? null;

        if(!device) {
            Notify('error', 'Device not found');
            return;
        }

        if(!usbManager.isDeviceConnected(device?.hardwareId ?? '')) {
            await usbManager.getDevices();
            if(usbManager.selectedDeviceId.toString() !== device?.hardwareId) {
                if(usbManager.selectedDeviceId) {
                    Notify('error', 'Please connect the correct device');
                    getConnectedDevices();
                }
                return;
            }
        } else {
            setShowLoadingScreen(true)
            await usbManager.setSelected();
        }
        
        setConnectedDevices(usbManager.connectedDevices);
        hidePopup();
        setSelectedDeviceID(device.id);
        setView('editDevice');
    }

    const handleAddNew = async () => {
        setView('addDevice');
    }

    const handleExisting = async (device: Device) => {
        setSelectedDeviceID(device.id);
        setView('editDevice');
    }

    const getView = () => {
        switch (dashboardView) {
            case 'deviceList':
                return <DeviceList setView={setView} handleAddNew={handleAddNew} editDevice={handleEditDevice}/>;
            case 'addDevice':
                return <AddDevice setView={setView} handleExisting={handleExisting} setSelectedDeviceID={setSelectedDeviceID}/>;
            case 'editDevice':
                return <EditDevice setView={setView} deviceID={selectedDeviceID!}/>;
            case 'FAQ':
                return <FAQ/>;
            case 'account':
                return <Account/>;
        }
    }

    return (
        <motion.div className="dashboard" initial={{ opacity: 0 }} animate={{ opacity: opacity }} transition={{ duration: 0.05 }} style={{height: '100%'}}>
            <img className="ktek-img dash" src={getThemeImage("K-TEK")} alt="ktek"/>
            <div className="dashboard-header">
                <div className="logo-wrapper">
                    <img src={getThemeImage("Knog")} alt="knog" />
                </div>
                <div className="header-right">
                    <div className="science-wrapper">
                        <img src={getThemeImage("UNKONVENT_SCIENCE")} alt="science"/>      
                    </div>
                    {/* <div style={{ marginTop: '18px' }}>
                        <Toggle 
                            value={theme !== 'dark'} 
                            setValue={toggleTheme}
                            withIcons={true}
                        />
                    </div> */}
                </div>
            </div>
            <div className="dashboard-content">
                <Grid container spacing={2} style={{ height: '85%' }}>
                    <Grid size={{ xs: 12/7 }} className="border-right">
                        <div className="dashboard-menu">
                            {menuItems.Internal.map((item: any) => (
                                <Button 
                                    key={item.text} 
                                    text={item.text} 
                                    onClick={item.onClick}
                                    className={`menu-button ${viewMenuItems[dashboardView] === item.text ? 'selected' : ''}`}/>
                            ))}
                            <div className="menu-title" style={{ marginTop: '50px' }}>VIA KNOG.COM</div>
                            {menuItems.External.map((item: any) => (
                                <Button key={item.text} text={item.text} onClick={item.onClick} className={'menu-button'}/>
                            ))}
                        </div>
                    </Grid>
                    <Grid size={{ xs: 12 - 12/7 }} sx={{ padding: '30px 30px 0 30px' }}>
                        {showLoadingScreen ? <div className="center-screen" style={{ width: '30%' }}>
                            <div style={{ width: '100%' }}>
                                <Loading isDeterminate={false} />
                            </div>
                        </div> : getView()}
                    </Grid>
                </Grid>
            </div>
        </motion.div>
    );
}

export default DashboardView;